import {
    Activity,
    type ComponentPropsWithRef,
    createContext,
    type MouseEvent,
    type PropsWithChildren,
    useEffect,
    useState,
} from "react";
import useInitializedContext from "@/hooks/use-initialized-context.ts";
import type { Nullable } from "@/types";
import { cn } from "@/utils/style.ts";
import Button, { type ButtonProps } from "./button.tsx";

type TabsContextType = { activeTab: string; changeTab: (tab: string) => void };
const TabsContext = createContext<Nullable<TabsContextType>>(null);

export const useTabs = () => useInitializedContext(TabsContext, "Tabs");

type TabsProviderProps = PropsWithChildren<{ initialTab: string; onTabChange?: (tab: string) => void }>;
function TabsProvider({ initialTab, children, onTabChange }: TabsProviderProps) {
    const [activeTab, setActiveTab] = useState<string>(initialTab);

    const changeTab = (tab: string) => {
        setActiveTab(tab);
        onTabChange?.(tab);
    };

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    return <TabsContext value={{ activeTab, changeTab }}>{children}</TabsContext>;
}

function Tabs({ children, className, ...props }: ComponentPropsWithRef<"div">) {
    return (
        <div {...props} className={cn("tabs", className)}>
            {children}
        </div>
    );
}

function TabsList({ children, className, ...props }: ComponentPropsWithRef<"div">) {
    return (
        <div {...props} className={cn("tabs__list", className)}>
            {children}
        </div>
    );
}

export type TabsTriggerProps = ButtonProps & {
    tab: string;
};
function TabsTrigger({ children, className, tab, onClick, ...props }: TabsTriggerProps) {
    const { activeTab, changeTab } = useTabs();

    const handleClick = (evt: MouseEvent<HTMLButtonElement>) => {
        changeTab(tab);
        onClick?.(evt);
    };

    return (
        <Button
            {...props}
            className={cn("tabs__trigger", className, activeTab === tab && "active")}
            onClick={handleClick}
        >
            {children}
        </Button>
    );
}

function TabContentInner({ children, className, ...props }: ComponentPropsWithRef<"div">) {
    return (
        <div {...props} className={cn("tabs__content", className)}>
            {children}
        </div>
    );
}

export type TabsContentProps = ComponentPropsWithRef<"div"> & { tab: string };
function TabContent({ children, className, tab, ...props }: TabsContentProps) {
    const { activeTab } = useTabs();

    return (
        <Activity mode={activeTab === tab ? "visible" : "hidden"}>
            <TabContentInner {...props} className={cn(activeTab === tab ? "active" : "", className)}>
                {children}
            </TabContentInner>
        </Activity>
    );
}

export { TabsProvider, Tabs, TabsList, TabsTrigger, TabContent };
